import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { environment } from '@environments/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { iframeUrlMiddleOffice } from '@shared/components/button-middle-office/config';
import { MiddleOfficeButtonType } from '@shared/components/button-middle-office/enums';
import { SafeResourceUrlPipe } from '@shared/pipe';
import { DataService } from '@shared/services';
import { LogsService } from '@shared/services/logs';
import { MiddleOfficeService } from '@shared/services/middle-office';
import { StoresMiddleOfficeCache } from '@shared/services/middle-office/models';
import { PageStore } from '@stores';
import { filter, take, tap } from 'rxjs';

type Action = 'DISCONNECT' | 'EDIT' | 'ADD';
type ButtonType = keyof typeof MiddleOfficeButtonType;
const actionMap: Readonly<Record<ButtonType, Action>> = {
  DISCONNECT: 'DISCONNECT',
  EDIT_HERO: 'EDIT',
  EDIT_SCHEDULES: 'EDIT',
  EDIT_REALISATION: 'EDIT',
  EDIT_TEAMS: 'EDIT',
  ADD_REALISATION: 'ADD',
} as const;

@Component({
  selector: 'app-button-middle-office',
  imports: [TranslatePipe, SafeResourceUrlPipe],
  templateUrl: './button-middle-office.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonMiddleOfficeComponent {
  readonly type = input.required<ButtonType>();
  readonly styleClass = input<string>('');

  readonly dialogRef = viewChild.required<ElementRef<HTMLDialogElement>>('dialogRef');

  readonly iframeUrl = signal<string>('');
  protected readonly currentActionType = computed<Action>(() => actionMap[this.type()]);
  protected readonly isDialogOpen = signal(false);
  private readonly middleOfficeService = inject(MiddleOfficeService);
  protected readonly isMiddleOfficeAvailable = this.middleOfficeService.isMiddleOfficeAvailable;
  private readonly currentStoreUuid = inject(PageStore).selectPath((state) => state.config?.context?.uuid);
  private readonly dataService = inject(DataService);
  private readonly logs = inject(LogsService);
  private readonly window = inject(DOCUMENT).defaultView as Window;
  private messageAbortController?: AbortController;

  executeCurrentAction(): void {
    const action = this.currentActionType();
    if (action === 'DISCONNECT') {
      this.middleOfficeService.logout();
    } else {
      this.dataService
        .get$<StoresMiddleOfficeCache>('middleOfficeStores')
        .pipe(
          take(1),
          filter((cache): cache is StoresMiddleOfficeCache => cache !== null),
          tap(({ token }) => {
            const typeKey = this.type() as Exclude<ButtonType, 'DISCONNECT'>;
            const mappedType = iframeUrlMiddleOffice[typeKey];
            const base = environment.API.endpoint.default;
            const url = `${base}/iframe/${this.currentStoreUuid()}/${mappedType}?token=${token}`;
            this.iframeUrl.set(url);
            this.logs.info('ButtonMiddleOfficeComponent', 'Opening modal', {
              storeUuid: this.currentStoreUuid(),
              type: this.type(),
            });
            this.dialogRef().nativeElement.showModal();
            this.isDialogOpen.set(true);
            this.attachWindowMessageListener();
          })
        )
        .subscribe();
    }
  }

  private onWindowMessage(event: MessageEvent): void {
    const data = event?.data as { event?: string } | null | undefined;
    if (!data || data.event !== 'closeIframeCallback') return;
    this.logs.info('ButtonMiddleOfficeComponent', 'closeIframeCallback received');
    this.closeDialog();
  }

  private closeDialog(): void {
    try {
      this.dialogRef().nativeElement.close();
    } catch {
      this.logs.info('ButtonMiddleOfficeComponent', 'Dialog already closed');
    }
    this.isDialogOpen.set(false);
    this.iframeUrl.set('');
    this.dataService
      .clear('CACHE')
      .pipe(
        take(1),
        tap(() => {
          this.window.location.reload();
        })
      )
      .subscribe();

    this.detachWindowMessageListener();
  }

  private attachWindowMessageListener(): void {
    if (this.messageAbortController) return;
    this.messageAbortController = new AbortController();
    this.window.addEventListener('message', (e) => this.onWindowMessage(e), {
      signal: this.messageAbortController.signal,
    });
  }

  private detachWindowMessageListener(): void {
    if (!this.messageAbortController) return;
    this.messageAbortController.abort();
    this.messageAbortController = undefined;
  }
}
